"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = require("lodash");
const react_1 = require("react");
function useLazyFetchingPagination(initialItems, options) {
    const { page: initialPage = 0, perPage = 3, total, fetchItems, staticItems = [], } = options;
    const insertStaticItems = (0, react_1.useCallback)((prevItems, prevStaticItems) => {
        const newItems = [...prevItems];
        prevStaticItems.forEach(({ item, position }) => {
            // Ensures the position of the static item is valid, either within the bounds of the existing list or at the end.
            if (position >= 0 && position <= prevItems.length) {
                newItems.splice(position, 0, item);
            }
        });
        return newItems;
    }, []);
    const [page, setPage] = (0, react_1.useState)(initialPage);
    const [items, setItems] = (0, react_1.useState)(insertStaticItems(initialItems, staticItems));
    const prevInitialItemsRef = (0, react_1.useRef)(initialItems);
    const prevStaticItemsRef = (0, react_1.useRef)(staticItems);
    const fetchPageItems = (0, react_1.useCallback)((targetPage) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (fetchItems && isFetchNeeded(targetPage, perPage, total, items)) {
            const firstNonFetchedItemIndex = targetPage * perPage +
                getNonEmptyItems(items.slice(targetPage * perPage, (targetPage + 1) * perPage)).length;
            const staticItemsNumber = getStaticItemsNumberBeforeIndex(staticItems, firstNonFetchedItemIndex);
            const nonStaticItemsNumber = firstNonFetchedItemIndex - staticItemsNumber;
            const pageToFetch = Math.floor(nonStaticItemsNumber / perPage);
            const result = yield fetchItems(pageToFetch, perPage);
            let newItems = [];
            if ((_a = result === null || result === void 0 ? void 0 : result.items) === null || _a === void 0 ? void 0 : _a.length) {
                newItems = result.items;
            }
            setItems(prevState => {
                const newState = [...prevState];
                newItems.forEach((item, itemIndex) => {
                    newState[pageToFetch * perPage + staticItemsNumber + itemIndex] = item;
                });
                return newState;
            });
        }
    }), [fetchItems, items, perPage, staticItems]);
    (0, react_1.useEffect)(() => {
        if (!(0, lodash_1.isEqual)(prevInitialItemsRef.current, initialItems) ||
            !(0, lodash_1.isEqual)(prevStaticItemsRef.current, staticItems)) {
            prevInitialItemsRef.current = initialItems;
            prevStaticItemsRef.current = staticItems;
            setItems(insertStaticItems(initialItems, staticItems));
            setPage(initialPage);
            fetchPageItems(initialPage);
        }
    }, [initialItems, staticItems]);
    (0, react_1.useEffect)(() => {
        fetchPageItems(page);
    }, [page]);
    return (0, react_1.useMemo)(() => {
        const maxPages = Math.ceil(total / perPage) - 1;
        return {
            items,
            page,
            hasMore: page < maxPages,
            nextPage() {
                setPage(prev => (prev < maxPages ? prev + 1 : prev));
            },
            prevPage() {
                setPage(prev => (prev > 0 ? prev - 1 : 0));
            },
            setPage(pageIndex) {
                if (pageIndex < 0 || pageIndex > maxPages) {
                    return;
                }
                setPage(pageIndex);
            },
            fetchPage(pageIndex) {
                fetchPageItems(pageIndex);
            },
        };
    }, [total, perPage, items, page]);
}
function getStaticItemsNumberBeforeIndex(staticItems, index) {
    if (!(staticItems === null || staticItems === void 0 ? void 0 : staticItems.length))
        return 0;
    return staticItems.filter(({ position }) => position < index).length;
}
function isFetchNeeded(targetPage, perPage, total, items) {
    const pageItems = items.slice(targetPage * perPage, (targetPage + 1) * perPage);
    return (!pageItems.length ||
        (pageItems.length < perPage && items.length < total) ||
        getNonEmptyItems(pageItems).length < pageItems.length);
}
function getNonEmptyItems(items) {
    const output = [];
    for (let i = 0; i < items.length; i++) {
        if (items.hasOwnProperty(i)) {
            output[i] = items[i];
        }
    }
    return output.filter(item => item !== undefined);
}
exports.default = useLazyFetchingPagination;
//# sourceMappingURL=useLazyFetchingPagination.js.map